/**
 * Business user roles issued by the auth service (`user.user_role`).
 */
export const UserRole = {
  superAdmin: 'super_admin',
  restaurantAdmin: 'restaurant_admin',
  restaurantManager: 'restaurant_manager',
  platformSupport: 'platform_support',
} as const;

/**
 * Route segments under `/main` a platform support user is allowed to open.
 * Support staff only troubleshoot menus and restaurant profile data, so
 * everything else in the sidebar stays out of reach for them.
 */
export const PLATFORM_SUPPORT_ROUTES: string[] = [
  'menu',
  'add-menu',
  'update-menu',
  'type-and-categories',
  'customization',
  'restaurant-profile',
];

/** Landing page for a platform support user once a restaurant is selected. */
export const PLATFORM_SUPPORT_DEFAULT_ROUTE = 'menu';

/** Sidebar items a platform support user can see (flat navigation). */
export const PLATFORM_SUPPORT_MENU_TITLES: string[] = [
  'Menu',
  'Types & Categories',
  'Customizations',
  'Restaurant Profile',
];

export function getLoggedInUser(): any {
  const user = localStorage.getItem('user');
  if (!user) {
    return null;
  }
  try {
    return JSON.parse(user);
  } catch {
    return null;
  }
}

export function getLoggedInUserRole(): string {
  return getLoggedInUser()?.user_role || '';
}

export function isPlatformSupportUser(): boolean {
  return getLoggedInUserRole() === UserRole.platformSupport;
}

export function isSuperAdminUser(): boolean {
  return getLoggedInUserRole() === UserRole.superAdmin;
}

/**
 * Roles restaurant-service lets read and change the hours and closed dates
 * (its HOURS_ROLES); a manager gets a 403 there.
 */
export function canEditHours(userRole: string): boolean {
  return (
    userRole === UserRole.superAdmin ||
    userRole === UserRole.restaurantAdmin ||
    userRole === UserRole.platformSupport
  );
}

/**
 * Roles that manage the restaurant's Fynkle Wall Profile and posts — the same
 * roles social-networking-service's `require_restaurant_staff` accepts
 * (FNF-1609, FNF-1607).
 */
export const SOCIAL_MEDIA_ROLES: string[] = [
  UserRole.superAdmin,
  UserRole.restaurantAdmin,
  UserRole.restaurantManager,
];

export function canManageSocialMedia(userRole: string): boolean {
  return SOCIAL_MEDIA_ROLES.includes(userRole);
}

/** Roles that pick a restaurant after login instead of owning one. */
export function canSelectRestaurant(userRole: string): boolean {
  return userRole === UserRole.superAdmin || userRole === UserRole.platformSupport;
}

/**
 * Checks a `/main` child route against the platform support allow list.
 * `path` may carry route params (e.g. `update-menu/123`); only the first
 * segment is matched.
 */
export function isRouteAllowedForPlatformSupport(path: string): boolean {
  const segment = (path || '').split('?')[0].split('/').filter(Boolean)[0] || '';
  return PLATFORM_SUPPORT_ROUTES.includes(segment);
}
